import { put, call, takeLatest, select, takeEvery } from 'redux-saga/effects';
import { progressStart, progressStop } from '../actions';

import ActionTypes from '../constants/ActionTypes';
import { fetchTestSettings, getActiveSession } from '../api/socket';

import { showErrorToast } from '../actions/toast';
import { getApiErrorData } from '../utils/helperFunction';
import { setFailedSocketEventsStatus } from '../actions/socket';

import {
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE,
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY,
} from '../constants/Socket';
import { captureException } from '../utils/sentry';
import { errorLogger, proctoringUBALogger } from '../utils/trackingUtil';
import { storeFailedEvents } from './proctor';
import { getAndUpdateWebsocketSequence } from '../utils/utils';

function* emitFailedEventsToSocket() {
  try {
    yield put(
      setFailedSocketEventsStatus({
        status: true,
      })
    );
    const [
      { assessmentId, assessmentType },
      { socket },
      { token },
    ] = yield select(state => [
      state.assessment,
      state.socket,
      state.testSolutionSet,
    ]);

    const failedSocketEventsData = sessionStorage.getItem(
      FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY
    );
    if (failedSocketEventsData) {
      const failedEventsValue = failedSocketEventsData
        ? JSON.parse(failedSocketEventsData)
        : FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE;

      // Check Local storage value is not empty
      if (Object.entries(failedEventsValue).length) {
        Object.entries(failedEventsValue).forEach(function([
          eventGuid,
          socketData,
        ]) {
          const { socketEventName, socketPayload } = socketData;
          if (socket?.connected && token) {
            // Todo: implement and use socket emit callback
            socket.emit(socketEventName, {
              ...socketPayload,
              assessmentId,
              assessmentType,
              token,
            });
            proctoringUBALogger('Failed events retriggered', socketEventName);
            const tempFailedEventsValue =
              JSON.parse(
                sessionStorage.getItem(FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY)
              ) || FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE;
            if (tempFailedEventsValue[eventGuid]) {
              delete tempFailedEventsValue[eventGuid];
              sessionStorage.setItem(
                FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY,
                JSON.stringify(tempFailedEventsValue)
              );
            }
          } else {
            // Store failed events in session storage when socket is not connected or token is missing
            storeFailedEvents({
              socketEventName,
              socketPayload: {
                ...socketPayload,
                assessmentId,
                assessmentType,
                token,
              },
            });
            proctoringUBALogger(
              'Failed events stored in session storage',
              socketEventName
            );
          }
        });
      }
    }
  } catch (error) {
    errorLogger(error);
    captureException(error);
  } finally {
    yield put(
      setFailedSocketEventsStatus({
        status: false,
      })
    );
  }
}

function* socketFetchSettings() {
  try {
    yield put(progressStart());
    const { assessmentId, assessmentType } = yield select(
      state => state.assessment
    );
    yield call(fetchTestSettings, {
      assessmentId,
      assessmentType,
    });
    yield put({
      type: `${ActionTypes.SOCKET_FETCH_SETTINGS}_SUCCESS`,
    });
  } catch (error) {
    captureException(error);
    const { toast } = yield select(state => state.appStatus);
    let errorInfo = {};
    if (error?.config?.url?.includes('validate-session')) {
      errorInfo = getApiErrorData(error, 'VDS');
    } else if (error?.config?.url?.includes('fetchsettings')) {
      errorInfo = getApiErrorData(error, 'WFS');
    }
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}

function* getSocketActiveSession(payload) {
  try {
    yield put(progressStart());
    const response = yield call(getActiveSession, payload);
    if (response.data) {
      yield put({
        type: `${ActionTypes.SET_ACTIVE_SESSION}_SUCCESS`,
        value: response.data,
      });
      payload.history.replace(`/multi-session`);
    } else {
      yield put({
        type: `${ActionTypes.GET_ACTIVE_SESSION}_SUCCESS`,
        value: response.data,
      });
    }
  } catch (error) {
    captureException(error);
    errorLogger(error, { ...payload });
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(error, 'GAS');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}

function* emitWebsocketEvent({ payload }) {
  try {
    const { eventData, eventName } = payload;
    const [
      { socket },
      { assessmentId, assessmentType },
      { token },
      { offsetTime },
    ] = yield select(state => [
      state.socket,
      state.assessment,
      state.testSolutionSet,
      state.appStatus,
    ]);
    const eventSequence = getAndUpdateWebsocketSequence(assessmentId);

    const socketPayload = {
      ...payload,
      token,
      assessmentId,
      assessmentType,
      eventData: { ...eventData, eventSequence },
      currentOffsetTime: offsetTime,
    };
    if (socket?.connected && token) {
      // Todo: implement and use socket emit callback
      socket.emit(eventName, socketPayload);
    } else {
      // Store failed events in localstorage. When socket is connected then emit all these events
      storeFailedEvents({
        socketEventName: eventName,
        socketPayload,
      });
    }
  } catch (error) {
    errorLogger(error);
    captureException(error);
  }
}

export default function* loadSocketSaga() {
  yield takeLatest(ActionTypes.SOCKET_FETCH_SETTINGS, socketFetchSettings);
  yield takeLatest(ActionTypes.GET_ACTIVE_SESSION, getSocketActiveSession);
  yield takeEvery(
    ActionTypes.EMIT_FAILED_EVENTS_TO_SOCKET,
    emitFailedEventsToSocket
  );
  yield takeEvery(ActionTypes.EMIT_WEBSOCKET_EVENT, emitWebsocketEvent);
}
